import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { Map, Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import { LinearProgress, Box, IconButton, Typography, Badge } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import 'maplibre-gl/dist/maplibre-gl.css';

import LegendMap from './map_legend.js';
import { useMapSync } from './map_syncro.js';
import MapSynchronizer from './sync_maps.js';
import {
  TILE_LAYER_URL,
  BOUNDS_RECTANGLE_STYLE,
  MASK_POLYGON_STYLE,
  MAP_CONFIG
} from '../../../utils/mapUtils.js';

const MapRightContent = ({
  mapRightLayer,
  cqlFecha,
  weekNumber,
  metricsNames,

  mapBounds,    // Recibido del padre
  rectBounds,   // Recibido del padre
}) => {
  const { mapRight: map } = useMap(); // Usamos el ID del mapa
  const [loading, setLoading] = useState(false);
  const [clickedInfo, setClickedInfo] = useState(null);

  // Efecto para el fit inicial
  useEffect(() => {
    if (map && mapBounds) {
      map.fitBounds(mapBounds, { padding: 60, duration: 800 });
    }
  }, [map, mapBounds]);

  const wmsParams = useMemo(() => ({
    service: 'WMS',
    request: 'GetMap',
    version: '1.1.1',
    layers: mapRightLayer,
    styles: '',
    format: 'image/png',
    transparent: 'true',
    width: '256',
    height: '256',
    srs: 'EPSG:3857',
    bbox: '{bbox-epsg-3857}',
    CQL_FILTER: `VALIDDATE='${cqlFecha}' AND WEEK_NUMBER=${weekNumber}`
  }), [mapRightLayer, cqlFecha, weekNumber]);

  const wmsUrl = useMemo(() => {
    const baseUrl = `${process.env.REACT_APP_API_URL}/proxy/wms/forecast-metrics`;
    const paramsString = Object.entries(wmsParams)
      .map(([k, v]) => `${k}=${v === '{bbox-epsg-3857}' ? v : encodeURIComponent(v)}`)
      .join('&');
    return `${baseUrl}?${paramsString}`;
  }, [wmsParams]);

  const onMapClick = useCallback(async (e) => {
    if (!map) return;

    const { lng, lat } = e.lngLat;
    const point = map.project(e.lngLat);
    const bounds = map.getBounds();
    const size = map.getCanvas();

    const baseUrl = `${process.env.REACT_APP_API_URL}/proxy/wms/forecast-metrics`;
    const featureInfoParams = new URLSearchParams({
      service: 'WMS',
      version: '1.1.1',
      request: 'GetFeatureInfo',
      layers: mapRightLayer,
      query_layers: mapRightLayer,
      info_format: 'application/json',
      x: Math.round(point.x),
      y: Math.round(point.y),
      width: size.width,
      height: size.height,
      srs: 'EPSG:4326',
      bbox: `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`,
      CQL_FILTER: `VALIDDATE='${cqlFecha}' AND WEEK_NUMBER=${weekNumber}`
    });

    try {
      setLoading(true);
      const resp = await axios.get(`${baseUrl}?${featureInfoParams.toString()}`);
      if (resp.data && resp.data.features && resp.data.features.length > 0) {
        setClickedInfo({ lngLat: e.lngLat, feature: resp.data.features[0] });
      } else {
        setClickedInfo(null);
      }
    } catch (err) {
      console.error("Error GetFeatureInfo:", err);
    } finally {
      setLoading(false);
    }
  }, [map, mapRightLayer, cqlFecha, weekNumber]);

  useEffect(() => {
    if (!map) return;
    map.on('click', onMapClick);
    return () => map.off('click', onMapClick);
  }, [map, onMapClick]);

  const maskGeoJson = useMemo(() => {
    if (!rectBounds) return null;
    const [[latN, lonW], [latS, lonE]] = rectBounds;
    const innerRing = [[lonW, latN], [lonE, latN], [lonE, latS], [lonW, latS], [lonW, latN]];
    const outerRing = [[-180, 90], [180, 90], [180, -90], [-180, -90], [-180, 90]];
    return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [outerRing, innerRing] } };
  }, [rectBounds]);

  return (
    <>
      <MapSynchronizer />
      {loading && (
        <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1000 }}>
          <LinearProgress />
        </Box>
      )}
      <Source id="wms-source" type="raster" tiles={[wmsUrl]} tileSize={256} key={wmsUrl}>
        <Layer id="wms-layer" type="raster" paint={{ 'raster-opacity': 0.8 }} />
      </Source>
      {maskGeoJson && (
        <Source type="geojson" data={maskGeoJson}>
          <Layer id="mask-layer" type="fill" paint={{ 'fill-color': MASK_POLYGON_STYLE.fillColor, 'fill-opacity': MASK_POLYGON_STYLE.fillOpacity }} />
        </Source>
      )}
      {rectBounds && (
        <Source type="geojson" data={{
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [rectBounds[0][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[0][0]]
            ]
          }
        }}>
          <Layer id="bounds-layer" type="line" paint={{ 'line-color': BOUNDS_RECTANGLE_STYLE.color, 'line-width': BOUNDS_RECTANGLE_STYLE.weight, 'line-dasharray': [3, 5] }} />
        </Source>
      )}
      {clickedInfo && (
        <Popup longitude={clickedInfo.lngLat.lng} latitude={clickedInfo.lngLat.lat} onClose={() => setClickedInfo(null)} anchor="bottom">
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2">Info:</Typography>
            <pre style={{ fontSize: '10px' }}>{JSON.stringify(clickedInfo.feature.properties, null, 2)}</pre>
          </Box>
        </Popup>
      )}
    </>
  );
};

const MapRight = ({
  cqlSevere,
  cqlFecha,
  mapRightLayer,
  weekNumber,
  issueTime,
  selectedModel,
  isInteractive = true,
  metricsNames
}) => {
  const [showLegend, setShowLegend] = useState(true);
  const { mapState } = useMapSync();
  const mapRef = useRef(null);

  // Mover cálculos de límites aquí para que handleReset pueda verlos
  const rectBounds = useMemo(() => {
    if (!metricsNames) return null;
    return metricsNames.includes('01')
      ? [[33, -16], [64, 45]]
      : [[33, -27], [73, 45]];
  }, [metricsNames]);

  const mapBounds = useMemo(() => {
    if (!rectBounds) return null;
    return [rectBounds[0][1], rectBounds[0][0], rectBounds[1][1], rectBounds[1][0]];
  }, [rectBounds]);

  const handleToggleLegend = () => setShowLegend(!showLegend);

  const handleReset = () => {
    if (mapRef.current && mapBounds) {
      mapRef.current.fitBounds(mapBounds, {
        padding: 60,
        duration: 1000
      });
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Map
        id="mapRight"
        onLoad={(e) => { mapRef.current = e.target; }}
        initialViewState={{
          latitude: mapState.center[0],
          longitude: mapState.center[1],
          zoom: mapState.zoom
        }}
        mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
        attributionControl={false}
        style={{ width: '100%', height: '100%' }}
        dragPan={isInteractive}
        scrollZoom={isInteractive}
        boxZoom={isInteractive}
        doubleClickZoom={isInteractive}
        touchZoomRotate={isInteractive}
        minZoom={MAP_CONFIG.minZoom}
        maxZoom={MAP_CONFIG.maxZoom}
        fadeDuration={0}
        collectResourceTiming={false}
      >
        <MapRightContent
          mapRightLayer={mapRightLayer}
          cqlFecha={cqlFecha}
          weekNumber={weekNumber}
          metricsNames={metricsNames}
          isInteractive={isInteractive}
          mapBounds={mapBounds}
          rectBounds={rectBounds}
        />
      </Map>

      <Badge
        badgeContent={
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
            RESET ZOOM
          </Typography>
        }
        color="error"
        overlap="circular"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 10,
          '& .MuiBadge-badge': {
            padding: '0 8px',
            height: '18px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            top: '0px',
            left: '-2px',
            transform: 'translateX(-100%)',
            borderRadius: '10px',
          },
        }}
      >
        <IconButton
          title='Reset view'
          onClick={handleReset}
          sx={{
            bgcolor: '#ffffff',
            color: '#38a477',
            '&:hover': { bgcolor: '#f5f5f5' },
            width: { xs: 28, md: 36 },
            height: { xs: 28, md: 36 },
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            borderRadius: 10,
            opacity: 0.9,
          }}
        >
          <HomeIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
        </IconButton>
      </Badge>

      <IconButton
        title='Information'
        onClick={handleToggleLegend}
        sx={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          zIndex: 10,
          bgcolor: '#ffffff',
          color: '#38a477df',
          '&:hover': { bgcolor: '#f5f5f5' },
          width: { xs: 28, md: 36 },
          height: { xs: 28, md: 36 },
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          borderRadius: 10,
          opacity: 0.9,
        }}
      >
        <InfoIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
      </IconButton>

      {showLegend && <LegendMap variant="right" />}
    </Box>
  );
};

export default MapRight;